import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { Intent } from '@blueprintjs/core';

import { Button } from 'components/buttons';
import { DataDisplayTable } from 'components/data-display';
import { FormTextInput } from 'components/form-fields';
import { ShowHide } from 'components/layout';
import { AlertConfirm, NotificationInline, NotificationToaster } from 'components/notifications';
import { Tooltip } from 'components/tooltip';
import { RulesEngineService } from 'services';

import {
    buildControlActions,
    buildControlFields,
    buildControlStatements,
    buildParentPbiBox,
    controlReadFailedText,
    formatClearDownResult,
    formatClearDownWarning,
    mapClearDownResponse,
    personalDataText,
    phaseOneKeptText,
    validateParentPbi,
    waitNoticeText
} from './diffViewerControl';
import { retryText } from './diffViewerFormatting';

const badRequestCode = 400;

const startErrorMessage = "Could not start the comparison. Please try again.";
const stopErrorMessage = "Could not stop the comparison. Please try again.";
const clearDownErrorMessage = "Could not clear down the comparison. Please try again.";

const defaultFunction = () => null;

// A 400 carries the API's own message; anything else, a timeout included, is reported without its body.
function showRequestError(error, message) {
    if (error?.status === badRequestCode) {
        NotificationToaster.show(Intent.WARNING, error.data);
        return;
    }

    NotificationToaster.show(Intent.DANGER, message);
}

function withTooltip(button, tooltip) {
    if (tooltip == null) {
        return button;
    }

    return (
        <Tooltip content={tooltip}>
            {button}
        </Tooltip>
    );
}

/**
 * Starts, stops and clears down the UnderTest comparison. Every action is followed by a fresh read of the
 * control, whatever its outcome, so the panel always ends on the API's real state.
 */
export function DiffViewerComparisonControl(
    {
        control = null,
        loading = false,
        failed = false,
        onRefresh = defaultFunction,
        onChanged = defaultFunction
    }
) {
    const [parentPbiText, setParentPbiText] = useState("");
    const [parentPbiError, setParentPbiError] = useState("");
    const [actionInFlight, setActionInFlight] = useState(false);
    const [confirmOpen, setConfirmOpen] = useState(false);
    // The page's filters can change while an action is in flight, so its outcome reaches the latest callback.
    const onChangedRef = useRef(onChanged);

    const box = buildParentPbiBox(control);
    const busy = actionInFlight || loading;
    const actions = buildControlActions(control, busy);
    const statements = buildControlStatements(control);
    const clearDownWarning = control == null ? "" : formatClearDownWarning(control.counts);

    useEffect(() => {
        setParentPbiText(buildParentPbiBox(control).value);
        setParentPbiError("");
    }, [control]);

    useEffect(() => {
        onChangedRef.current = onChanged;
    });

    // Any action can change the comparison the page shows. Only a 400 is a refusal; after any other failure, a
    // timeout included, the action may still have gone through.
    function runAction(request, errorMessage, onSuccess = defaultFunction) {
        setActionInFlight(true);

        request().then((response) => {
            onSuccess(response);
            onChangedRef.current();
        }, (error) => {
            showRequestError(error, errorMessage);

            if (error?.status !== badRequestCode) {
                onChangedRef.current();
            }
        }).finally(() => {
            setActionInFlight(false);
            setConfirmOpen(false);
            onRefresh();
        });
    }

    function onParentPbiChange(event) {
        setParentPbiText(event.target.value);
        setParentPbiError("");
    }

    function onStart() {
        const error = validateParentPbi(parentPbiText);
        setParentPbiError(error);

        if (error !== "") {
            return;
        }

        const parentPbiId = Number(parentPbiText.trim());
        runAction(() => RulesEngineService.startUnderTestComparison(parentPbiId), startErrorMessage);
    }

    function onStop() {
        runAction(() => RulesEngineService.stopUnderTestComparison(), stopErrorMessage);
    }

    function onClearDownConfirm() {
        runAction(() => RulesEngineService.clearDownUnderTestComparison(), clearDownErrorMessage, (response) => {
            NotificationToaster.show(Intent.SUCCESS, formatClearDownResult(mapClearDownResponse(response)));
        });
    }

    return (
        <div id="rules-engine-diff-control" className="spacer-bottom">
            <h3 id="rules-engine-diff-control-heading">UnderTest comparison</h3>

            <ShowHide
                evaluator={failed}
                show={(
                    <div id="rules-engine-diff-control-failed" className="inline-items spacer-bottom-small">
                        <span>{controlReadFailedText}</span>
                        <Button id="rules-engine-diff-control-retry" text={retryText} icon="refresh" large={false} onClick={onRefresh} />
                    </div>
                )}
                hide={(
                    <DataDisplayTable
                        loading={loading && control == null}
                        displayInRows={false}
                        data={buildControlFields(control)}
                    />
                )}
            />

            <div className="spacer-bottom-small">
                <p id="rules-engine-diff-control-wait-notice" className="description-text">{waitNoticeText}</p>
            </div>

            <ShowHide
                evaluator={statements.length > 0}
                show={(
                    <div id="rules-engine-diff-control-statements" className="notification-inline-holder">
                        {statements.map((statement) => (
                            <NotificationInline
                                key={statement}
                                intent="warning"
                                show
                                allowClose={false}
                                text={statement}
                            />
                        ))}
                    </div>
                )}
            />

            <FormTextInput
                id="rules-engine-diff-control-pbi"
                headingText="Parent PBI"
                value={parentPbiText}
                disabled={box.disabled}
                helperText={box.helperText}
                dangerHelperText={parentPbiError}
                onChange={onParentPbiChange}
            />

            <div className="button-row">
                {withTooltip((
                    <Button
                        id="rules-engine-diff-control-start"
                        text="Start"
                        icon="play"
                        disabled={!actions.startEnabled}
                        onClick={onStart}
                    />
                ), actions.startTooltip)}
                <Button
                    id="rules-engine-diff-control-stop"
                    text="Stop"
                    icon="stop"
                    disabled={!actions.stopEnabled}
                    onClick={onStop}
                />
                {withTooltip((
                    <Button
                        id="rules-engine-diff-control-clear-down"
                        text="Clear down"
                        icon="trash"
                        disabled={!actions.clearDownEnabled}
                        onClick={() => setConfirmOpen(true)}
                    />
                ), actions.clearDownTooltip)}
            </div>

            <AlertConfirm
                isOpen={confirmOpen}
                title="Clear down the UnderTest comparison"
                saving={busy}
                onConfirm={onClearDownConfirm}
                onCancel={() => setConfirmOpen(false)}
            >
                <p id="rules-engine-diff-control-confirm-counts">{clearDownWarning}</p>
                <p>{personalDataText}</p>
                <p>{phaseOneKeptText}</p>
            </AlertConfirm>
        </div>
    );
}

DiffViewerComparisonControl.propTypes = {
    control: PropTypes.object,
    loading: PropTypes.bool,
    failed: PropTypes.bool,
    onRefresh: PropTypes.func,
    onChanged: PropTypes.func
};
